import { Component, Input, Output, EventEmitter, inject, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-document-viewer',
  standalone: true,
  imports: [CommonModule, PdfViewerModule],
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent {
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);

  @Input() fileName: string = '';
  @Input() fileUrl: string = '';
  @Input() extension: string = '';
  @Input() visible: boolean = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onDownload = new EventEmitter<void>();

  objectUrl: string | null = null;
  pdfSrc: Uint8Array | null = null; // Almacena el PDF como array de bytes para evitar restricciones de blob en iOS Safari
  safeUrl: SafeResourceUrl | null = null;
  safeImgUrl: SafeUrl | null = null;
  isLoading: boolean = false;

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  openInNewTab() {
    if (this.objectUrl) {
      window.open(this.objectUrl, '_blank');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fileUrl'] || changes['visible']) {
      if (this.visible && this.fileUrl) {
        this.loadSecureUrl();
      } else if (!this.visible) {
        this.cleanup();
      }
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.pdfSrc = null;
    this.safeUrl = null;
    this.safeImgUrl = null;
    this.textContent = null;
  }

  textContent: string | null = null;
  
  private async loadSecureUrl() {
    // Si la URL viene apuntando a localhost/127.0.0.1 del backend, pero el usuario está accediendo
    // desde otro host (como una IP de red local '172.16.1.147' en el celular), reemplazamos 
    // dinámicamente el host para que apunte al servidor correcto.
    let targetUrl = this.fileUrl;
    if (typeof window !== 'undefined') {
      const currentHost = window.location.hostname;
      if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
        targetUrl = targetUrl
          .replace('//localhost:', `//${currentHost}:`)
          .replace('//127.0.0.1:', `//${currentHost}:`);
      }
    }

    const type = this.fileType;
    console.log('loadSecureUrl - Detectado tipo:', type, 'URL original:', this.fileUrl, 'URL objetivo:', targetUrl);

    this.isLoading = true;
    this.cleanup();

    try {
      console.log('Descargando archivo desde:', targetUrl);
      const blob = await firstValueFrom(this.http.get(targetUrl, { responseType: 'blob' })) as Blob;
      if (blob) {
        this.objectUrl = URL.createObjectURL(blob);
        
        if (type === 'image') {
          this.safeImgUrl = this.sanitizer.bypassSecurityTrustUrl(this.objectUrl);
        } else if (type === 'text') {
          // Leer el blob como texto para renderizarlo con estilo
          this.textContent = await blob.text();
        } else if (type === 'pdf') {
          // iOS Safari (WebKit) tiene restricciones de seguridad severas con las URL de blob (`blob:`)
          // en iframes y visores canvas. Para evitar esto, leemos el blob como un ArrayBuffer
          // y lo convertimos en un Uint8Array (array de bytes) en memoria, el cual ng2-pdf-viewer
          // soporta de forma nativa sin restricciones de origen.
          const arrayBuffer = await blob.arrayBuffer();
          this.pdfSrc = new Uint8Array(arrayBuffer);
          console.log('PDF convertido a Uint8Array con éxito. Tamaño:', this.pdfSrc.length, 'bytes');
        } else {
          const viewerUrl = this.objectUrl;
          this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
        }
      }
    } catch (error: any) {
      console.error('Error cargando el archivo:', error);
      alert('Error al descargar el archivo: ' + (error.message || error.statusText || 'Error de red.'));
    } finally {
      this.isLoading = false;
    }
  }

  get fileType(): 'pdf' | 'image' | 'text' | 'other' {
    const url = (this.fileUrl || '').toLowerCase();
    const ext = (this.extension || '').toLowerCase().replace('.', '');
    
    // Prioridad 1: Si la URL ya es un PDF (por conversión en el backend), tratarlo como PDF
    if (url.includes('.pdf')) return 'pdf';
    
    // Prioridad 2: Extensión original
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext)) return 'image';
    if (['txt', 'md', 'rtf'].includes(ext)) return 'text';
    return 'other';
  }

  close() {
    this.onClose.emit();
  }

  download() {
    this.onDownload.emit();
  }
}
